import { Button } from 'components/ui/button';
import { buttonStyle } from 'components/buttons/buttonStyle';
/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import { addDays, subDays } from 'date-fns';
import { useForm, useWatch } from 'react-hook-form';
import { useLazyGetMinOfficesWithFiltersQuery } from 'store/api/offices';
import { useLazyGetMinTeamsListWithFiltersQuery } from 'store/api/teams';
import { EmploymentForm } from 'types/employees';
import { NamedEntityMinDto } from 'types/other';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { t } from 'utils/getTranslations';
import DateInput from '../../forms/inputs/rhf-inputs/DateInput';
import SelectInput from '../../forms/inputs/rhf-inputs/SelectInput';
import { ModalFooter } from '../ModalFooter';
import { ModalHeader } from '../ModalHeader';

interface Props {
    onClose: () => void;
    onSave?: (employment: EmploymentForm) => void;
    onDelete?: (recordId: number) => void;
    initialData?: EmploymentForm;
    titleId?: string;
    jobRoleList: NamedEntityMinDto[];
    jobPositionList: NamedEntityMinDto[];
}

const defaultValues: EmploymentForm = {
    recordId: 0,
    jobRole: null,
    jobPosition: null,
    office: null,
    coreTeam: null,
    validFrom: formatDateStringInput(new Date()),
    validTo: null,
    error: false,
};

const EmploymentModal = (props: Props) => {
    const {
        titleId = 'employees.employment.form.edit',
        initialData,
        jobRoleList,
        jobPositionList,
        onSave,
        onClose,
        onDelete,
    } = props;

    const { handleSubmit, control, setValue, getValues } = useForm<EmploymentForm>({
        defaultValues: initialData ? { ...defaultValues, ...initialData } : defaultValues,
        values: initialData,
    });

    const [validFrom, validTo] = useWatch({ control, name: ['validFrom', 'validTo'] });

    const handleOnChange = (data: EmploymentForm) => {
        if (onSave) onSave(data);
        onClose();
    };

    const handleOnDelete = (recordId: number) => {
        if (onDelete) onDelete(recordId);
        onClose();
    };

    const onSaveEmployment = () => {
        handleSubmit((data) => handleOnChange(data))();
    };

    const deleteButton = onDelete ? (
        <Button
            {...buttonStyle({ variant: 'outlined', color: 'error' }, 'text-sm font-normal')}
            key="delete"
            onClick={() => handleOnDelete(getValues('recordId'))}
        >
            {t('delete')}
        </Button>
    ) : undefined;

    return (
        <>
            <ModalHeader title={t(titleId)} onCancel={onClose} />
            <div className="h-full overflow-y-auto px-8 py-[38px] max-sm:px-4 max-sm:py-6">
                <div className="flex flex-col gap-4">
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="employees.employment.form.job-role"
                        required
                        name="jobRole"
                        options={jobRoleList ?? []}
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                        placeholder="employees.employment.form.job-role"
                    />
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="employees.employment.form.job-position"
                        required
                        name="jobPosition"
                        options={jobPositionList ?? []}
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                        placeholder="employees.employment.form.job-position"
                    />
                    <DynamicSelectInput
                        setValue={setValue}
                        control={control}
                        label="employees.employment.form.office"
                        required
                        name="office"
                        paginated={{
                            fetch: useLazyGetMinOfficesWithFiltersQuery,
                            searchField: 'name',
                        }}
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                        placeholder="employees.employment.form.office"
                    />
                    <DynamicSelectInput
                        setValue={setValue}
                        control={control}
                        label="employees.employment.form.core-team"
                        required
                        name="coreTeam"
                        paginated={{
                            fetch: useLazyGetMinTeamsListWithFiltersQuery,
                            searchField: 'name',
                        }}
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                        placeholder="employees.employment.form.core-team"
                    />
                    <DateInput
                        maxDate={validTo ? subDays(new Date(validTo), 1) : undefined}
                        name="validFrom"
                        control={control}
                        label="employees.employment.form.valid-from"
                        required
                    />
                    <DateInput
                        minDate={validFrom ? addDays(new Date(validFrom), 1) : undefined}
                        name="validTo"
                        control={control}
                        label="employees.employment.form.valid-to"
                    />
                </div>
            </div>
            <ModalFooter extraButton={deleteButton} onSave={onSaveEmployment} onCancel={onClose} />
        </>
    );
};

export default EmploymentModal;
